<template>
  <div class="flex flex-col w-full h-full gap-2 overflow-y-auto">
    <span class="mb-1 font-medium">שם הכרטיסייה</span>
    <InputText v-model="form.name" />

    <span class="mt-2 mb-1 font-medium">שירותים</span>
    <select-service
      v-model="selectedServices"
      @services-selected="onServicesSelected"
    />

    <span class="mt-2 mb-1 font-medium">כמות מימושים</span>
    <input-number v-model="form.totalUses" :min="1" :max="100" />

    <span class="mt-2 mb-1 font-medium">מחיר</span>
    <input-number
      v-model="form.price"
      :min="1"
      :maxFractionDigits="0"
      mode="currency"
      currency="ILS"
      locale="he-IL"
    />

    <span class="mt-2 mb-1 font-medium">תוקף (חודשים)</span>
    <input-number v-model="form.validityMonths" :min="1" :max="60" />

    <settings-toggle
      class="mt-3"
      :status="form.showOnWebsite ?? true"
      :disabled="!hasPayments"
      title="הצג באתר העסק"
      :description="hasPayments ? 'כשכבוי, הכרטיסייה זמינה לשיוך ידני בלבד ולא מוצגת באתר לרכישה' : 'לא ניתן להציג באתר — לא חובר אמצעי תשלום בעסק'"
      @value-changed="(val) => form.showOnWebsite = val"
    />

    <drawer-footer
      class="mt-auto"
      :approveDisable="!canSubmit"
      :approveLoading="saving"
      approve-label="שמירת שינויים"
      cancelLabel="ביטול"
      @approve-click="save"
      @cancel-click="closeDrawer"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, reactive, ref } from 'vue';
import { IBundlePackTemplate, IService } from '@torim-app/types';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import { useAdminStore } from '@app/stores';
import SelectService from '@app/components/admin/components/SelectService.vue';

const props = defineProps({
  template: {
    type: Object as PropType<IBundlePackTemplate>,
    required: true
  }
});

const emit = defineEmits(['updated']);
const toast = useToast();
const { closeDrawer } = useDrawer();
const adminStore = useAdminStore();

const form = reactive<IBundlePackTemplate>({ ...props.template });
const selectedServices = ref<IService[]>([]);
const saving = ref(false);

// Without a connected payment provider a bundle can't be bought online, so it can never be
// shown on the site — force it off and lock the toggle (set here in setup so the toggle,
// which reads `status` only once, initializes to the correct value).
const hasPayments = computed(() => !!adminStore.getBusinessSiteData?.hasPayments);
if (!hasPayments.value) form.showOnWebsite = false;

const canSubmit = computed(() =>
  !!form.name?.trim() &&
  (form.serviceIds?.length ?? 0) > 0 &&
  !!form.totalUses && form.totalUses > 0 &&
  form.price != null && form.price >= 0
);

const onServicesSelected = (services: IService[]) => {
  selectedServices.value = services;
  form.serviceIds = services.map(s => s.id);
};

onMounted(() => {
  const allServices = adminStore.businessSiteData?.services ?? [];
  selectedServices.value = allServices.filter(s => props.template.serviceIds.includes(s.id));
});

const save = async () => {
  if (!canSubmit.value) return;
  saving.value = true;
  try {
    const updated = await BundlePacksApi.updateTemplate(props.template._id!, form);
    toast.success('נשמר');
    emit('updated', updated);
    closeDrawer();
  } catch (e) {
    toast.error('שגיאה בשמירה');
  } finally {
    saving.value = false;
  }
};

</script>
